import { Button, Input, Select } from '@aecom-ai-engineering/react-components';
import { useGetOracleProjectQuery, useUpdateProjectMutation } from '@consigli/hooks';
import {
  type Country,
  isSharedOracleProject,
  type Project,
  type ProjectConstructionType,
  type UpdateProjectRequest,
} from '@consigli/types';
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { OracleProjectField } from '@/components/oracle-project-field';
import { LOCKED_FIELD } from '@/util/locked-field';
import { countryOptions } from '@/util/project-fields';
import { useCanManageProject } from '@/util/use-project-permissions';

import { AdminControlsPanel } from './admin-controls-panel';
import { ConstructionTypeField } from './construction-type-field';

/** The frame's 560 column, inside the 800 container it centres in. */
const COLUMN = 'w-[560px] max-w-full';

/** 14/20 weight 500 — the frame's `paragraph small/medium`. */
const LABEL = 'text-[14px] leading-5 font-medium text-[var(--ds-color-text-primary)]';

/**
 * 14/20 regular in the frame's secondary. `raw-stone-600` and not the token of
 * that name — the same ramp drift people-table.tsx and the tab bar document.
 */
const HELPER = 'text-[14px] leading-5 text-[color:var(--ds-color-raw-stone-600)]';

/** The editable shape. `oracleId` is set at project creation and never changes. */
interface SettingsDraft {
  address: string;
  constructionTypes: ProjectConstructionType[];
  country: Country;
  name: string;
}

const draftOf = (project: Project): SettingsDraft => ({
  address: project.address ?? '',
  constructionTypes: project.constructionTypes,
  country: project.country,
  name: project.name,
});

interface FieldProps {
  /** Receives the helper's id, for the control's `aria-describedby`. */
  children: (describedBy: string | undefined) => ReactNode;
  helper?: string;
  id: string;
  label: string;
}

/**
 * One labelled field: the frame's label, its helper text, then the control.
 *
 * The visible label is a `<span>`, not a `<label>`. The library controls take
 * their accessible name as a prop and render it hidden — `Input.label` is
 * required and says so — so a real `<label>` here would be a second name for the
 * same control. Call sites pass the same string to both.
 *
 * The helper cannot go through a prop at all, since the frame puts it between
 * the label and the control, so it is rendered here and tied on with
 * `aria-describedby`.
 */
const Field: FC<FieldProps> = ({ children, helper, id, label }) => {
  const helperId = helper === undefined ? undefined : `${id}-help`;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <span className={LABEL}>{label}</span>
        {helper !== undefined && (
          <span className={HELPER} id={helperId}>
            {helper}
          </span>
        )}
      </div>
      {children(helperId)}
    </div>
  );
};

interface SettingsTabProps {
  project: Project;
}

/**
 * The project Settings tab: Figma 11759-160478.
 *
 * Edits are held until **Save Changes** and then sent together. The earlier
 * frame had no submit at all and this one does, which settles the question that
 * shape left open: nothing is written as a side effect of typing, blurring or
 * ticking, so an edit can be reconsidered by leaving the page.
 *
 * Only the fields that actually differ are sent. `updateProject` PATCHes, so
 * writing the whole object would clobber whatever a colleague changed in
 * between — and sending an untouched field would do the same for that field.
 *
 * **Who may edit is not a styling question.** Every control is disabled, Save is
 * absent, and the Admin Controls section is absent, unless `useCanManageProject`
 * says this user may change this project — superuser, org admin, or admin *of
 * this project*, which is the rule the project management list already applies
 * and the one the backend grants `change_project_object` for. The API does not
 * enforce it on PATCH today, so this is the only thing standing between a plain
 * member and an edit that would succeed; see the hook.
 */
export const SettingsTab: FC<SettingsTabProps> = ({ project }) => {
  const { t } = useTranslation();
  const [updateProject, { isLoading: isSaving }] = useUpdateProjectMutation();
  const canManage = useCanManageProject(project.id);

  const countries = useMemo(() => countryOptions(t), [t]);
  // Whitespace-only counts as unset, as the save path treats a blank address.
  const oracleId = project.oracleId?.trim() ?? '';

  // The same lookup OracleProjectField makes to name the stored number, so it
  // is one request. It decides whether the name is Oracle's.
  const { data: oracleProject } = useGetOracleProjectQuery(
    { projectNumber: oracleId },
    { skip: oracleId === '' },
  );
  /*
   * A Direct project was created with Oracle's name and keeps it, as the create
   * page locks it. Only an Indirect or Bid & Proposal project, whose name the
   * user typed, may be renamed. Until Oracle answers, or when it cannot (the
   * endpoints refuse anyone outside AECOM), the project is not known to be
   * shared, so the name stays locked rather than open.
   */
  const isNameLocked =
    oracleId !== '' && !isSharedOracleProject(oracleProject?.projectTypeClassCode);

  const stored = useMemo(() => draftOf(project), [project]);
  const [draft, setDraft] = useState<SettingsDraft>(stored);

  // Resync when the stored values change: after a save lands, and when a
  // refetch brings someone else's edit. Keyed on the values rather than the
  // object, which is a new identity on every refetch and would otherwise
  // discard an edit in progress.
  const storedSignature = JSON.stringify(stored);
  useEffect(() => setDraft(draftOf(project)), [storedSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = useCallback(
    <K extends keyof SettingsDraft>(key: K, value: SettingsDraft[K]) =>
      setDraft((current) => ({ ...current, [key]: value })),
    [],
  );

  /**
   * The fields that differ from what is stored. Text is compared trimmed,
   * because trailing whitespace is not an edit anyone means to make.
   */
  const changed = useMemo(
    () =>
      (Object.keys(stored) as (keyof SettingsDraft)[]).filter((key) => {
        const [a, b] = [draft[key], stored[key]];

        if (Array.isArray(a) && Array.isArray(b)) return a.join() !== b.join();
        return String(a).trim() !== String(b).trim();
      }),
    [draft, stored],
  );

  // The name is required and unique per organisation, so there is no empty
  // state to submit. Blocking Save is the whole validation: the frame draws no
  // error slot, and this is the one field that cannot be cleared.
  const nameIsEmpty = draft.name.trim() === '';
  const canSave = changed.length > 0 && !nameIsEmpty && !isSaving;

  const onSave = useCallback(async () => {
    // address is nullable, so cleared means null, not "".
    const patch: UpdateProjectRequest = {
      ...(changed.includes('name') && { name: draft.name.trim() }),
      ...(changed.includes('address') && { address: draft.address.trim() || null }),
      ...(changed.includes('country') && { country: draft.country }),
      ...(changed.includes('constructionTypes') && {
        constructionTypes: draft.constructionTypes,
      }),
    };

    try {
      await updateProject({ projectId: project.id, data: patch }).unwrap();
      toast.success(t('project-overview.settings-saved'));
    } catch {
      toast.error(t('project-overview.settings-save-failed'));
    }
  }, [changed, draft, project.id, t, updateProject]);

  return (
    // py-8 matches the Agents and Users & Invites tabs, which share this shell.
    <div className="flex flex-col items-center py-8">
      <div className={`${COLUMN} flex flex-col gap-6`}>
        <h2 className="text-xl leading-6 font-medium text-[var(--ds-color-text-primary)]">
          {t('project-overview.settings')}
        </h2>

        <Field
          helper={isNameLocked ? t('project-creation.project-name-auto-filled') : undefined}
          id="project-name"
          label={t('project-creation.project-name-label')}
        >
          {(describedBy) => (
            <Input
              aria-describedby={describedBy}
              className={isNameLocked && canManage ? LOCKED_FIELD : undefined}
              disabled={!canManage}
              invalid={nameIsEmpty}
              label={t('project-creation.project-name-label')}
              onChange={(event) => set('name', event.target.value)}
              readOnly={isNameLocked}
              size="md"
              value={draft.name}
            />
          )}
        </Field>

        <Field id="project-address" label={t('project-creation.project-address-label')}>
          {() => (
            <Input
              disabled={!canManage}
              label={t('project-creation.project-address-label')}
              onChange={(event) => set('address', event.target.value)}
              size="md"
              value={draft.address}
            />
          )}
        </Field>

        {/* Only a project created with an Oracle ID shows the field. The ID is
            set at project creation and never changes, so an empty one would be
            a disabled box with nothing in it. */}
        {oracleId !== '' && (
          <Field
            helper={t('project-overview.settings-oracle-id-hint')}
            id="project-oracle-id"
            label={t('project-overview.settings-oracle-id')}
          >
            {(describedBy) => (
              // Read-only for every role, since this tab only shows the ID.
              <OracleProjectField
                describedBy={describedBy}
                disabled
                label={t('project-overview.settings-oracle-id')}
                onChange={() => undefined}
                value={oracleId}
              />
            )}
          </Field>
        )}

        <Field
          helper={t('project-creation.country-help-text')}
          id="project-country"
          label={t('project-creation.country-label')}
        >
          {(describedBy) => (
            <Select
              aria-describedby={describedBy}
              // Select.css caps its root at `min(100%, --ds-space-52)`, which is
              // 208 — sized for the toolbar it was drawn for, not for a 560
              // form column, and it left this one field short of every other.
              // A utility rather than a CSS override: Tailwind's utilities layer
              // sorts after the design system's components layer, so `w-full`
              // wins on layer order without having to out-specify anything.
              // `menuWidth="trigger"` then carries the menu with it.
              className="w-full"
              disabled={!canManage}
              indicator="single"
              items={countries}
              label={t('project-creation.country-label')}
              menuWidth="trigger"
              onValueChange={({ value }) => {
                // The control is not deselectable, and a cleared value would be
                // an invalid country anyway.
                if (value[0] !== undefined) set('country', value[0] as Country);
              }}
              value={[draft.country]}
            />
          )}
        </Field>

        {/* No helper text, and the frame no longer draws one here — the previous
            version's was the Country helper pasted. */}
        <Field id="project-type" label={t('project-creation.construction-type-label')}>
          {() => (
            <ConstructionTypeField
              disabled={!canManage}
              onChange={(next) => set('constructionTypes', next)}
              value={draft.constructionTypes}
            />
          )}
        </Field>

        {canManage && (
          <>
            {/* Left-aligned under the grid, as the frame places it, rather than
                right-aligned to the column. */}
            <div>
              <Button disabled={!canSave} onClick={() => void onSave()} size="md" variant="solid">
                {t(
                  isSaving ? 'project-overview.settings-saving' : 'project-overview.settings-save',
                )}
              </Button>
            </div>

            <hr className="border-t border-[var(--ds-color-border-primary)]" />

            <AdminControlsPanel project={project} />
          </>
        )}
      </div>
    </div>
  );
};
